import {
  QuoteRoundParty,
  QuoteStatus,
  type Currency,
  RequestForQuoteStatus,
  type Money,
} from '~/graphql/graphql';
import {
  buildProductGroups,
  type GroupableItem,
  type ProductGroup,
} from '~/pages/rfq-view/itemGroups';
import type { ExistingQuoteItem } from '~/pages/quote-response/useQuoteResponseDraft';
import {
  isClosedRequestForQuoteStatus,
  partitionArchivedQuotes,
} from '~/utils/requestForQuoteClosed';
import { isQuoteAwardable } from './quoteAwarding';

export type NegotiableQuote = {
  id: string;
  status: QuoteStatus;
  negotiationAwaitingParty?: QuoteRoundParty | null;
};

export type CounterableQuote = NegotiableQuote & {
  currency?: Currency | null;
  leadTimeDays?: number | null;
  sellingOrganizationFacility?: { id: string } | null;
  offeredPickupPriceForTally?: Money | null;
  offeredDeliveryPriceForTally?: Money | null;
  items: Array<
    ExistingQuoteItem & {
      platformProductsLength: { id: string };
      order?: { id: string } | null;
      ordered?: boolean;
    }
  >;
};

export type NegotiatingQuote = Pick<
  NegotiableQuote,
  'status' | 'negotiationAwaitingParty'
>;

// A draft awaits its seller too, but nothing is on the table until the seller submits.
function isNegotiationOpen(quote: NegotiatingQuote): boolean {
  return (
    quote.status !== QuoteStatus.InProgress &&
    quote.negotiationAwaitingParty != null
  );
}

function isAwaitingParty(
  quote: NegotiatingQuote,
  party: QuoteRoundParty
): boolean {
  return isNegotiationOpen(quote) && quote.negotiationAwaitingParty === party;
}

// Ordered lines cannot be walked away from, so a quote with any of them cannot be declined.
export function isDeclinable(quote: NegotiatingQuote): boolean {
  return (
    isNegotiationOpen(quote) && quote.status !== QuoteStatus.PartiallyAccepted
  );
}

export function isAwaitingSellerResponse(
  quote: Pick<NegotiableQuote, 'status'>
): boolean {
  return quote.status === QuoteStatus.InProgress;
}

function isCounteredByBuyer(quote: NegotiatingQuote): boolean {
  return (
    (quote.status === QuoteStatus.Submitted ||
      quote.status === QuoteStatus.PartiallyAccepted) &&
    isAwaitingParty(quote, QuoteRoundParty.Seller)
  );
}

export function quoteStatusLabelKey(quote: NegotiatingQuote): string {
  return `rfq_view.quote_status.${isCounteredByBuyer(quote) ? 'COUNTERED_BY_BUYER' : quote.status}`;
}

export function hasRoutineQuoteStatus(quote: NegotiatingQuote): boolean {
  return (
    quote.status === QuoteStatus.InProgress ||
    (quote.status === QuoteStatus.Submitted && !isCounteredByBuyer(quote))
  );
}

export type QuoteNegotiationContext = Readonly<{
  groups: ProductGroup[];
  usesWholeRequestPrice: boolean;
  hasCannedTally: boolean;
  requestForQuoteCurrency: Currency | null | undefined;
}>;

export type QuoteNegotiationContexts = ReadonlyMap<
  string,
  QuoteNegotiationContext
>;

type NegotiableRequestForQuote = Readonly<{
  id: string;
  items: GroupableItem[];
  useTallyPrice: boolean;
  cannedTally?: { id: string } | null;
  currency?: Currency | null;
  quotes: ReadonlyArray<{ status: QuoteStatus; items: GroupableItem[] }>;
}>;

export function negotiationContextsFor(
  requestsForQuote: ReadonlyArray<NegotiableRequestForQuote>
): QuoteNegotiationContexts {
  return new Map(
    requestsForQuote.map((requestForQuote) => [
      requestForQuote.id,
      {
        groups: buildProductGroups(
          requestForQuote.items,
          partitionArchivedQuotes(requestForQuote.quotes).activeQuotes.flatMap(
            (quote) => quote.items
          )
        ),
        usesWholeRequestPrice: requestForQuote.useTallyPrice,
        hasCannedTally: Boolean(requestForQuote.cannedTally),
        requestForQuoteCurrency: requestForQuote.currency,
      },
    ])
  );
}

export function negotiationContextOf(
  contexts: QuoteNegotiationContexts,
  requestForQuoteId: string
): QuoteNegotiationContext {
  const context = contexts.get(requestForQuoteId);
  if (!context) {
    throw new Error(
      `No negotiation context for request for quote ${requestForQuoteId}`
    );
  }
  return context;
}

export type QuoteWithRequestStatus = Readonly<{
  status: QuoteStatus;
  negotiationAwaitingParty?: QuoteRoundParty | null;
  requestForQuote: { status: RequestForQuoteStatus };
}>;

export type AcceptState = 'hidden' | 'disabled' | 'enabled';

export type BuyerActionSet = Readonly<{
  accept: AcceptState;
  awaitingSeller: boolean;
  respondForSeller: boolean;
  counter: boolean;
  comment: boolean;
  chooseContacts: boolean;
  archive: boolean;
}>;

export type SellerActionSet = Readonly<{
  acceptCounter: boolean;
  counter: boolean;
  decline: boolean;
  comment: boolean;
}>;

const FINAL_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QuoteStatus.Accepted,
  QuoteStatus.Declined,
  QuoteStatus.Archived,
]);

const ARCHIVABLE_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  QuoteStatus.InProgress,
  QuoteStatus.Submitted,
  QuoteStatus.Declined,
]);

function isCommentable(
  status: QuoteStatus,
  requestForQuoteStatus: RequestForQuoteStatus
): boolean {
  return (
    status !== QuoteStatus.Archived &&
    requestForQuoteStatus !== RequestForQuoteStatus.Archived
  );
}

function acceptState(status: QuoteStatus, isOpen: boolean): AcceptState {
  if (isAwaitingSellerResponse({ status }) || FINAL_STATUSES.has(status)) {
    return 'hidden';
  }
  return isOpen && isQuoteAwardable(status) ? 'enabled' : 'disabled';
}

export function buyerQuoteActions(
  quote: QuoteWithRequestStatus
): BuyerActionSet {
  const { status, requestForQuote } = quote;
  const isOpen = !isClosedRequestForQuoteStatus(requestForQuote.status);
  return {
    accept: acceptState(status, isOpen),
    awaitingSeller: isOpen && isAwaitingSellerResponse({ status }),
    respondForSeller: isOpen && isAwaitingParty(quote, QuoteRoundParty.Seller),
    counter: isOpen && isAwaitingParty(quote, QuoteRoundParty.Buyer),
    comment: isCommentable(status, requestForQuote.status),
    chooseContacts: isOpen && !FINAL_STATUSES.has(status),
    archive: isOpen && ARCHIVABLE_STATUSES.has(status),
  };
}

export function sellerQuoteActions(
  quote: QuoteWithRequestStatus
): SellerActionSet {
  const { status, requestForQuote } = quote;
  const isOpen = !isClosedRequestForQuoteStatus(requestForQuote.status);
  const isSellersTurn =
    isOpen && isAwaitingParty(quote, QuoteRoundParty.Seller);
  return {
    acceptCounter: isSellersTurn,
    counter: isSellersTurn,
    decline: isOpen && isDeclinable(quote),
    comment: isCommentable(status, requestForQuote.status),
  };
}
